import type { Locale } from '$lib/i18n/types';
import type { AvailableLanguageTag } from '$paraglide/runtime';
import * as m from '$paraglide/messages';
import {
	type CalendarDate,
	DateFormatter,
	getLocalTimeZone,
	parseDate,
} from '@internationalized/date';

const relativeTimeCache = new Map();
function getRelativeTimeFormatter(lang: string) {
	if (!relativeTimeCache.has(lang)) {
		relativeTimeCache.set(lang, new Intl.RelativeTimeFormat(lang, { numeric: 'auto' }));
	}

	return relativeTimeCache.get(lang);
}

const dateTimeCache = new Map();
function getDateTimeFormatter(lang: string) {
	if (!dateTimeCache.has(lang)) {
		dateTimeCache.set(lang, new Intl.DateTimeFormat(lang, { month: 'long', day: 'numeric' }));
	}

	return dateTimeCache.get(lang);
}

export function formatPastTime(date: Date, lang: AvailableLanguageTag): [string, boolean] {
	const now: Date = new Date();
	const diffInMilliseconds = now.getTime() - date.getTime();
	const minutesAgo = Math.floor(diffInMilliseconds / (60 * 1000));
	const hoursAgo = Math.floor(diffInMilliseconds / (60 * 60 * 1000));
	const daysAgo = Math.floor(diffInMilliseconds / (24 * 60 * 60 * 1000));

	const relativeTimeFormat = getRelativeTimeFormatter(lang);
	const dateTimeFormat = getDateTimeFormatter(lang);

	if (minutesAgo < 60) {
		return [relativeTimeFormat.format(-minutesAgo, 'minute'), true];
	} else if (hoursAgo < 24) {
		return [relativeTimeFormat.format(-hoursAgo, 'hour'), true];
	} else if (daysAgo < 15) {
		return [relativeTimeFormat.format(-daysAgo, 'day'), true];
	} else {
		return [
			m.poi_card__updated(
				{ date: dateTimeFormat.format(date) },
				{
					languageTag: lang,
				},
			),
			false,
		];
	}
}

const dateShortTimeCache = new Map();
export function getShortDateTimeFormatter(language: string) {
	if (!dateShortTimeCache.has(language)) {
		dateShortTimeCache.set(
			language,
			new DateFormatter(language, { month: 'short', day: 'numeric' }),
		);
	}

	return dateShortTimeCache.get(language);
}

export function getDaysBetween(dateStart: CalendarDate, dateEnd: CalendarDate) {
	const day = 24 * 60 * 60 * 1000;

	return Math.round(
		(dateEnd.toDate(getLocalTimeZone()).getTime()
			- dateStart.toDate(getLocalTimeZone()).getTime())
			/ day,
	);
}

export function transformStringDateToCalendarDate(date: string | undefined) {
	if (date) {
		return parseDate(date);
	}
}

export const timeZonesByLocale: Record<Locale, string> = {
	'de-AT': 'Europe/Vienna',
	'fr-BE': 'Europe/Brussels',
	'nl-BE': 'Europe/Brussels',
	'de-CH': 'Europe/Zurich',
	'fr-CH': 'Europe/Zurich',
	'it-CH': 'Europe/Zurich',
	'en-US': 'America/New_York',
	'de-DE': 'Europe/Berlin',
	'es-ES': 'Europe/Madrid',
	'fr-FR': 'Europe/Paris',
	'it-IT': 'Europe/Rome',
	'en-IE': 'Europe/Dublin',
	'nl-NL': 'Europe/Amsterdam',
	'pt-PT': 'Europe/Lisbon',
	'en-GB': 'Europe/London',
};
